import { startAuthentication } from '@simplewebauthn/browser';
import { SKIP_UNAUTH_HEADER, type ApiClient } from '@sentinel/api-client';

/** What a step-up WebAuthn attempt ended up doing. */
export type StepUpWebAuthnResult =
  /** The session is elevated; the caller should resolve the queue entry. */
  | { kind: 'success' }
  /** The user dismissed the authenticator prompt. Not an error — say nothing. */
  | { kind: 'cancelled' }
  /** The account has no credential the server will accept, or WebAuthn is off. */
  | { kind: 'no_credentials' }
  /** The assertion reached the server and was refused. */
  | { kind: 'rejected' }
  /** Anything else: a transport failure or an unusable response. */
  | { kind: 'error' };

/** The begin response is undeclared in the OpenAPI document, so narrow it here. */
interface BeginBody {
  challenge_id?: string;
  options?: unknown;
}

/**
 * Runs a step-up WebAuthn assertion end to end.
 *
 * There is no `webauthn/finish` route: the assertion is submitted to the same
 * `/auth/step-up/verify` the password path uses, with the challenge id echoed
 * back. On success the server re-sets the elevated access cookie (or returns
 * the elevated token for bearer callers), and the blocked request is replayed
 * by the api-client middleware.
 */
export async function runStepUpWebAuthn(
  apiClient: ApiClient,
  targetAcr: string,
): Promise<StepUpWebAuthnResult> {
  let begin;
  try {
    begin = await apiClient.POST('/auth/step-up/webauthn/begin', {});
  } catch {
    return { kind: 'error' };
  }
  const beginStatus = begin.response?.status;
  if (begin.error || (beginStatus !== undefined && beginStatus >= 400)) {
    // 400 is the server saying this account has nothing to assert with, or
    // that WebAuthn is disabled. Both mean "pick another method".
    return beginStatus === 400 ? { kind: 'no_credentials' } : { kind: 'error' };
  }

  const body = (begin.data as BeginBody | undefined) ?? {};
  if (!body.challenge_id || !body.options) return { kind: 'error' };

  // The server returns the full request-options shape wrapped in
  // `{ mediation, publicKey: { challenge, allowCredentials, ... } }`
  // (webauthn-rs standard). simplewebauthn's `startAuthentication` wants the
  // inner `publicKey` block as `optionsJSON`. Without this unwrap the call
  // hangs, because `optionsJSON.challenge` is undefined and the library never
  // proceeds to `navigator.credentials.get()`.
  const wrapped = body.options as { publicKey?: unknown } | undefined;
  const optionsJSON = (
    wrapped && typeof wrapped === 'object' && 'publicKey' in wrapped
      ? wrapped.publicKey
      : (wrapped as unknown)
  ) as Parameters<typeof startAuthentication>[0]['optionsJSON'];

  let assertion: Awaited<ReturnType<typeof startAuthentication>>;
  try {
    assertion = await startAuthentication({ optionsJSON });
  } catch (err) {
    const name = (err as { name?: string } | null)?.name;
    if (name === 'NotAllowedError' || name === 'AbortError') return { kind: 'cancelled' };
    return { kind: 'error' };
  }

  try {
    const res = await apiClient.POST('/auth/step-up/verify', {
      // A refused assertion answers 401, which the unauth middleware would
      // otherwise read as session expiry and turn into a sign-out.
      headers: { [SKIP_UNAUTH_HEADER]: '1' },
      body: {
        target_acr: targetAcr,
        webauthn_challenge_id: body.challenge_id,
        webauthn_credential: assertion,
      },
    });
    const status = res.response?.status;
    if (res.error || (status !== undefined && status >= 400)) {
      return status === 401 || status === 403 ? { kind: 'rejected' } : { kind: 'error' };
    }
    return { kind: 'success' };
  } catch {
    return { kind: 'error' };
  }
}
